import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  EyeIcon,
  ClipboardIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { extendedManagementApi } from '../api/managementService';
import type { ApiClient } from '../api/managementService';
import type { Customer } from '../context/ManagementContext';
import AdvancedEntitySelector from './AdvancedEntitySelector';
import DataTable from './DataTable';
import ActionButton from './ActionButton';
import EntitySelectorButton from './EntitySelectorButton';
import { useDebounce } from '../hooks/useDebounce';
import ApiClientRateLimits from './ApiClientRateLimits';
import ApiClientAnalytics from './ApiClientAnalytics';

const apiClientSchema = z.object({
  clientId: z.string().min(3, 'Client ID must be at least 3 characters'),
  clientSecret: z.string().min(8, 'Client secret must be at least 8 characters'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  scopes: z.array(z.string()).min(1, 'At least one scope is required'),
  customerId: z.string().min(1, 'Customer is required'),
});

const updateApiClientSchema = z.object({
  name: z.string().min(1, 'Name is required').optional(),
  description: z.string().optional(),
  scopes: z.array(z.string()).min(1, 'At least one scope is required').optional(),
  customerId: z.string().min(1, 'Customer is required').optional(),
});

type ApiClientFormData = z.infer<typeof apiClientSchema>;
type UpdateApiClientFormData = z.infer<typeof updateApiClientSchema>;

const AVAILABLE_SCOPES = [
  { value: 'telemetry.read', label: 'Telemetry Read Access' },
  { value: 'telemetry.write', label: 'Telemetry Write Access' },
  { value: 'device.read', label: 'Device Read Access' },
  { value: 'device.write', label: 'Device Write Access' },
  { value: 'management.admin', label: 'Management Admin Access' }
];

export default function ApiClientManagement() {
  // const { } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL parameters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isSecretOpen, setIsSecretOpen] = useState(false);
  const [isRateLimitsOpen, setIsRateLimitsOpen] = useState(false);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ApiClient | null>(null);
  const [rateLimitsClient, setRateLimitsClient] = useState<ApiClient | null>(null);
  const [analyticsClient, setAnalyticsClient] = useState<ApiClient | null>(null);
  const [viewingSecret, setViewingSecret] = useState<{clientId: string; clientSecret: string} | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCustomerSelectorOpen, setIsCustomerSelectorOpen] = useState(false);
  const [selectedCustomerForForm, setSelectedCustomerForForm] = useState<Customer | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Function to update URL with current filter state
  const updateUrlParams = useCallback(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params, { replace: true });
  }, [searchTerm, currentPage, setSearchParams]);

  // Update URL parameters whenever filter state changes
  useEffect(() => {
    updateUrlParams();
  }, [updateUrlParams]);

  const createForm = useForm<ApiClientFormData>({
    resolver: zodResolver(apiClientSchema),
    defaultValues: {
      scopes: ['telemetry.read'],
    },
  });

  const updateForm = useForm<UpdateApiClientFormData>({
    resolver: zodResolver(updateApiClientSchema),
  });

  // Removed unused customers query

  // Fetch API clients
  const { data: clientsData, isLoading, error: queryError } = useQuery({
    queryKey: ['apiClients', currentPage, debouncedSearchTerm],
    queryFn: () => extendedManagementApi.getApiClients(currentPage, 10, debouncedSearchTerm),
  });

  // Fetch usage stats for all clients
  const { data: usageStats } = useQuery({
    queryKey: ['apiClientUsageStats'],
    queryFn: () => extendedManagementApi.getClientsSummary(),
    refetchInterval: 60000, // Refetch every minute
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: extendedManagementApi.createApiClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiClients'] });
      setIsCreateOpen(false);
      createForm.reset();
      setError(null);
    },
    onError: (error: any) => {
      setError(error.message || 'Failed to create API client');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateApiClientFormData }) =>
      extendedManagementApi.updateApiClient(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiClients'] });
      setIsEditOpen(false);
      setEditingClient(null);
      updateForm.reset();
      setError(null);
    },
    onError: (error: any) => {
      setError(error.message || 'Failed to update API client');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: extendedManagementApi.deleteApiClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiClients'] });
      setError(null);
    },
    onError: (error: any) => {
      setError(error.message || 'Failed to delete API client');
    },
  });

  const viewSecretMutation = useMutation({
    mutationFn: (clientId: string) => {
      return extendedManagementApi.viewApiClientSecret(clientId).then((data: any) => {
        const client = clientsData?.clients.find((c: any) => c.id === clientId);
        return {
          clientId: client?.clientId || '',
          clientSecret: data.clientSecret
        };
      });
    },
    onSuccess: (data) => {
      setViewingSecret(data);
      setIsSecretOpen(true);
    },
    onError: (error: any) => {
      setError(error.message || 'Failed to view client secret');
    },
  });

  const regenerateSecretMutation = useMutation({
    mutationFn: (clientId: string) => {
      return extendedManagementApi.regenerateApiClientSecret(clientId).then((data: any) => {
        const client = clientsData?.clients.find((c: any) => c.id === clientId);
        return {
          clientId: client?.clientId || '',
          clientSecret: data.clientSecret
        };
      });
    },
    onSuccess: (data) => {
      setViewingSecret(data);
      setIsSecretOpen(true);
      queryClient.invalidateQueries({ queryKey: ['apiClients'] });
    },
    onError: (error: any) => {
      setError(error.message || 'Failed to regenerate client secret');
    },
  });

  const generateClientId = () => {
    const name = createForm.getValues('name') || 'client';
    const prefix = name.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 8) || 'client';
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    createForm.setValue('clientId', `${prefix}_${timestamp}_${random}`);
  };

  const generateClientSecret = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let secret = '';
    for (let i = 0; i < 64; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    createForm.setValue('clientSecret', secret);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleCreate = (data: ApiClientFormData) => {
    createMutation.mutate(data);
  };

  const handleUpdate = (data: UpdateApiClientFormData) => {
    if (editingClient) {
      updateMutation.mutate({ id: editingClient.id, data });
    }
  };

  const handleDelete = (client: ApiClient) => {
    if (confirm(`Are you sure you want to delete "${client.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(client.id);
    }
  };

  const openEditModal = (client: ApiClient) => {
    setEditingClient(client);
    setSelectedCustomerForForm(client.customer as Customer || null);
    updateForm.reset({
      name: client.name,
      description: client.description || '',
      scopes: client.scopes,
      customerId: client.customerId || '',
    });
    setIsEditOpen(true);
  };

  // Helper to get usage stats for a client
  const getClientStats = (clientId: string) => {
    return usageStats?.find(stat => stat.clientId === clientId);
  };

  const apiClientColumns = [
    {
      key: 'name',
      header: 'Client',
      render: (client: ApiClient) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{client.name}</div>
          {client.description && (
            <div className="text-sm text-gray-500">{client.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'clientId',
      header: 'Client ID',
      render: (client: ApiClient) => (
        <div className="flex items-center gap-2">
          <code className="text-sm bg-gray-100 px-2 py-1 rounded">{client.clientId}</code>
          <button
            onClick={() => copyToClipboard(client.clientId)}
            className="text-gray-400 hover:text-gray-600"
            title="Copy Client ID"
          >
            <ClipboardIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    {
      key: 'scopes',
      header: 'Scopes',
      render: (client: ApiClient) => (
        <div className="flex flex-wrap gap-1">
          {client.scopes.map((scope) => (
            <span
              key={scope}
              className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
            >
              {scope}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (client: ApiClient) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            client.isActive
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {client.isActive ? (
            <>
              <CheckCircleIcon className="w-3 h-3 mr-1" />
              Active
            </>
          ) : (
            <>
              <XCircleIcon className="w-3 h-3 mr-1" />
              Inactive
            </>
          )}
        </span>
      ),
    },
    {
      key: 'usage',
      header: 'Today',
      render: (client: ApiClient) => {
        const stats = getClientStats(client.clientId);
        if (!stats) {
          return <span className="text-sm text-gray-400">No data</span>;
        }
        return (
          <div className="text-sm">
            <div className="text-gray-900 font-medium">{stats.requestsToday.toLocaleString()} req</div>
            <div className="flex gap-2 text-xs text-gray-500">
              {stats.errorsToday > 0 && (
                <span className="text-red-600">{stats.errorsToday} err</span>
              )}
              {stats.rateLimitHitsToday > 0 && (
                <span className="text-orange-600">{stats.rateLimitHitsToday} limits</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'lastUsedAt',
      header: 'Last Used',
      render: (client: ApiClient) => {
        const stats = getClientStats(client.clientId);
        const lastUsed = stats?.lastUsedAt || client.lastUsedAt;
        return lastUsed
          ? new Date(lastUsed).toLocaleString()
          : 'Never';
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (client: ApiClient) => (
        <div className="flex items-center gap-1">
          <ActionButton
            onClick={() => {
              setAnalyticsClient(client);
              setIsAnalyticsOpen(true);
            }}
            icon={PresentationChartLineIcon}
            variant="edit"
            title="View analytics"
          />
          <ActionButton
            onClick={() => openEditModal(client)}
            icon={PencilIcon}
            variant="edit"
            title="Edit client"
          />
          <ActionButton
            onClick={() => {
              setRateLimitsClient(client);
              setIsRateLimitsOpen(true);
            }}
            icon={ChartBarIcon}
            variant="edit"
            title="Configure rate limits"
          />
          <ActionButton
            onClick={() => viewSecretMutation.mutate(client.id)}
            icon={EyeIcon}
            variant="edit"
            title="View secret"
          />
          <ActionButton
            onClick={() => {
              if (confirm('Are you sure you want to regenerate the client secret? The old secret will no longer work.')) {
                regenerateSecretMutation.mutate(client.id);
              }
            }}
            icon={KeyIcon}
            variant="edit"
            title="Regenerate secret"
          />
          <ActionButton
            onClick={() => handleDelete(client)}
            icon={TrashIcon}
            variant="delete"
            title="Delete client"
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Client Management</h1>
          <p className="text-gray-600">Manage API authentication credentials</p>
        </div>
        <button
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create API Client
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search API clients by name or client ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {(error || queryError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || (queryError as any)?.message}
        </div>
      )}

      {/* API Clients Table */}
      <DataTable
        data={clientsData?.clients || []}
        columns={apiClientColumns}
        isLoading={isLoading}
        error={queryError}
        pagination={clientsData?.pagination}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        emptyStateTitle="No API clients found"
        emptyStateDescription="Create a new API client to get started"
      />

      {/* Create API Client Dialog */}
      <Dialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Create API Client
              </DialogTitle>
            </div>

            <div className="px-6 py-4">
              <form onSubmit={createForm.handleSubmit(handleCreate)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer *
                  </label>
                  <input type="hidden" {...createForm.register('customerId')} />
                  <EntitySelectorButton
                    selectedEntity={selectedCustomerForForm}
                    onSelect={() => setIsCustomerSelectorOpen(true)}
                    onClear={() => {
                      setSelectedCustomerForForm(null);
                      createForm.setValue('customerId', '');
                      createForm.clearErrors('customerId');
                    }}
                    placeholder="Select a customer..."
                    icon={BuildingOfficeIcon}
                    getEntityName={(customer) => customer.name}
                    getEntityDescription={(customer) => customer.description || 'No description'}
                    iconColor="text-blue-600"
                  />
                  {createForm.formState.errors.customerId && (
                    <p className="mt-1 text-sm text-red-600">{createForm.formState.errors.customerId.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    {...createForm.register('name')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., My Mobile App"
                  />
                  {createForm.formState.errors.name && (
                    <p className="mt-1 text-sm text-red-600">{createForm.formState.errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">
                    Client ID *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="clientId"
                      {...createForm.register('clientId')}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder="e.g., myapp_abc123_def456"
                    />
                    <button
                      type="button"
                      onClick={generateClientId}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
                    >
                      Generate
                    </button>
                  </div>
                  {createForm.formState.errors.clientId && (
                    <p className="mt-1 text-sm text-red-600">{createForm.formState.errors.clientId.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="clientSecret" className="block text-sm font-medium text-gray-700 mb-1">
                    Client Secret *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="clientSecret"
                      {...createForm.register('clientSecret')}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder="64-character secure secret"
                    />
                    <button
                      type="button"
                      onClick={generateClientSecret}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
                    >
                      Generate
                    </button>
                  </div>
                  {createForm.formState.errors.clientSecret && (
                    <p className="mt-1 text-sm text-red-600">{createForm.formState.errors.clientSecret.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    {...createForm.register('description')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe what this client will be used for..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permissions
                  </label>
                  <div className="space-y-2">
                    {AVAILABLE_SCOPES.map((scope) => (
                      <label key={scope.value} className="flex items-center">
                        <input
                          type="checkbox"
                          value={scope.value}
                          {...createForm.register('scopes')}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{scope.label}</span>
                      </label>
                    ))}
                  </div>
                  {createForm.formState.errors.scopes && (
                    <p className="mt-1 text-sm text-red-600">{createForm.formState.errors.scopes.message}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsCreateOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create API Client'}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Edit API Client Dialog */}
      <Dialog open={isEditOpen} onClose={() => setIsEditOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Edit API Client
              </DialogTitle>
            </div>

            <div className="px-6 py-4">
              <form onSubmit={updateForm.handleSubmit(handleUpdate)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer *
                  </label>
                  <input type="hidden" {...updateForm.register('customerId')} />
                  <EntitySelectorButton
                    selectedEntity={selectedCustomerForForm}
                    onSelect={() => setIsCustomerSelectorOpen(true)}
                    onClear={() => {
                      setSelectedCustomerForForm(null);
                      updateForm.setValue('customerId', '');
                      updateForm.clearErrors('customerId');
                    }}
                    placeholder="Select a customer..."
                    icon={BuildingOfficeIcon}
                    getEntityName={(customer) => customer.name}
                    getEntityDescription={(customer) => customer.description || 'No description'}
                    iconColor="text-blue-600"
                  />
                  {updateForm.formState.errors.customerId && (
                    <p className="mt-1 text-sm text-red-600">{updateForm.formState.errors.customerId.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    id="edit-name"
                    {...updateForm.register('name')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter client name"
                  />
                  {updateForm.formState.errors.name && (
                    <p className="mt-1 text-sm text-red-600">{updateForm.formState.errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="edit-description"
                    {...updateForm.register('description')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe what this client will be used for..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permissions
                  </label>
                  <div className="space-y-2">
                    {AVAILABLE_SCOPES.map((scope) => (
                      <label key={scope.value} className="flex items-center">
                        <input
                          type="checkbox"
                          value={scope.value}
                          {...updateForm.register('scopes')}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{scope.label}</span>
                      </label>
                    ))}
                  </div>
                  {updateForm.formState.errors.scopes && (
                    <p className="mt-1 text-sm text-red-600">{updateForm.formState.errors.scopes.message}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsEditOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Updating...' : 'Update API Client'}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* View Secret Dialog */}
      <Dialog open={isSecretOpen} onClose={() => setIsSecretOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-lg font-semibold text-gray-900 flex items-center">
                <KeyIcon className="h-5 w-5 mr-2 text-green-600" />
                API Client Credentials
              </DialogTitle>
            </div>

            <div className="px-6 py-4">
              <div className="space-y-4">
                <div className="bg-green-50 border-l-4 border-green-400 p-4">
                  <p className="text-sm text-green-700">
                    <strong>API Client Credentials:</strong> Copy these credentials for your application configuration.
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client ID
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <code className="text-sm font-mono break-all text-gray-800 leading-relaxed">
                        {viewingSecret?.clientId}
                      </code>
                      <button
                        onClick={() => copyToClipboard(viewingSecret?.clientId || '')}
                        className="ml-3 flex-shrink-0 text-blue-600 hover:text-blue-800 p-1"
                        title="Copy Client ID to clipboard"
                      >
                        <ClipboardIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client Secret
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <code className="text-sm font-mono break-all text-gray-800 leading-relaxed">
                        {viewingSecret?.clientSecret}
                      </code>
                      <button
                        onClick={() => copyToClipboard(viewingSecret?.clientSecret || '')}
                        className="ml-3 flex-shrink-0 text-blue-600 hover:text-blue-800 p-1"
                        title="Copy Client Secret to clipboard"
                      >
                        <ClipboardIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setIsSecretOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Customer Selector Modal */}
      <AdvancedEntitySelector
        isOpen={isCustomerSelectorOpen}
        onClose={() => setIsCustomerSelectorOpen(false)}
        onSelect={(entity) => {
          const customer = entity as Customer;
          setSelectedCustomerForForm(customer);
          if (isCreateOpen) {
            createForm.setValue('customerId', customer.id);
            createForm.clearErrors('customerId');
          } else if (isEditOpen) {
            updateForm.setValue('customerId', customer.id);
            updateForm.clearErrors('customerId');
          }
          setIsCustomerSelectorOpen(false);
        }}
        entityType="customer"
        title="Select Customer"
        selectedId={selectedCustomerForForm?.id}
      />

      {/* Rate Limits Configuration Modal */}
      {rateLimitsClient && (
        <ApiClientRateLimits
          isOpen={isRateLimitsOpen}
          onClose={() => {
            setIsRateLimitsOpen(false);
            setRateLimitsClient(null);
          }}
          clientId={rateLimitsClient.id}
          clientName={rateLimitsClient.name}
        />
      )}

      {/* Analytics Modal */}
      {analyticsClient && (
        <ApiClientAnalytics
          isOpen={isAnalyticsOpen}
          onClose={() => {
            setIsAnalyticsOpen(false);
            setAnalyticsClient(null);
          }}
          clientId={analyticsClient.id}
          clientName={analyticsClient.name}
        />
      )}
    </div>
  );
}